/**
 * BalloonEventNotifier
 *
 * Decoupled event bus for notifying UI components (map, list, detail page)
 * when a balloon event has been created, updated, or cloned.
 *
 * Uses native CustomEvent on `window` so listeners work across independent
 * React trees (map widget, site app, simple-creator) without shared context.
 *
 * @example
 * // Fire after publish
 * BalloonEventNotifier.notify({ id: '...', slug: '...', lng: 16.4, lat: 48.2, mode: 'create' });
 *
 * // Listen (e.g. inside useEffect)
 * const unsub = BalloonEventNotifier.listen((payload) => { ... });
 * return () => unsub();
 */

export interface BalloonEventNotification {
  /** UUID of the created/updated event */
  id: string;
  /** URL-friendly slug (returned by API) */
  slug?: string;
  /** Longitude of the event location */
  lng?: number;
  /** Latitude of the event location */
  lat?: number;
  /** Whether this was a create, edit, or clone operation */
  mode: 'create' | 'edit' | 'clone';
}

export class BalloonEventNotifier {
  static readonly EVENT_NAME = 'balloon-event-change';

  /**
   * Dispatch a notification that a balloon event was created/updated.
   * Safe to call even if no listeners are registered.
   */
  static notify(payload: BalloonEventNotification): void {
    if (typeof window === 'undefined') return;
    const event = new CustomEvent<BalloonEventNotification>(
      BalloonEventNotifier.EVENT_NAME,
      { detail: payload },
    );
    console.log('[BalloonEventNotifier] notify', payload);
    window.dispatchEvent(event);
  }

  /**
   * Subscribe to balloon event change notifications.
   * @returns An unsubscribe function — call it in useEffect cleanup.
   */
  static listen(
    callback: (payload: BalloonEventNotification) => void,
  ): () => void {
    if (typeof window === 'undefined') return () => {};
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<BalloonEventNotification>).detail;
      callback(detail);
    };
    window.addEventListener(BalloonEventNotifier.EVENT_NAME, handler);
    return () =>
      window.removeEventListener(BalloonEventNotifier.EVENT_NAME, handler);
  }
}
